<script setup lang="ts">
import { watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { formatAed } from '@shared/utils'

interface Props {
  modelValue: number
  minTokens: number
  tokensAvailable: number
  tokenPrice: number
  investmentValue: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const tokenFormatter = new Intl.NumberFormat('en-US')

watch(
  () => props.minTokens,
  (min) => {
    if (props.modelValue < min) emit('update:modelValue', min)
  },
  { immediate: true },
)

const decrement = () => {
  if (props.modelValue > props.minTokens) emit('update:modelValue', props.modelValue - 1)
}

const increment = () => {
  if (props.modelValue < props.tokensAvailable) emit('update:modelValue', props.modelValue + 1)
}

const onInputChange = (event: Event) => {
  const input = event.target as HTMLInputElement
  const raw = Number(input.value)
  if (isNaN(raw) || raw < props.minTokens) {
    emit('update:modelValue', props.minTokens)
    input.value = String(props.minTokens)
  } else {
    emit('update:modelValue', Math.floor(raw))
  }
}
</script>

<template>
  <Box direction="col" align="center" gap="2" class="token-stepper-wrap">
    <Box align="center" justify="between" class="token-stepper">
      <Box
        is="button"
        align="center"
        justify="center"
        type="button"
        class="token-stepper__btn"
        :disabled="modelValue <= minTokens"
        @click="decrement"
      >
        <PiIcon name="minus" />
      </Box>
      <Box direction="col" align="center" gap="1" class="token-stepper__count-block">
        <!-- Raw <input type="number"> — inline numeric stepper input, no kit citizen yet §19 exception. -->
        <input
          class="token-stepper__count-input"
          type="number"
          :value="modelValue"
          :min="minTokens"
          :max="tokensAvailable"
          @change="onInputChange"
        />
        <Text type="body-sm" is="span" class="token-stepper__count-sub">
          = {{ formatAed(investmentValue) }}
        </Text>
      </Box>
      <Box
        is="button"
        align="center"
        justify="center"
        type="button"
        class="token-stepper__btn"
        :disabled="modelValue >= tokensAvailable"
        @click="increment"
      >
        <PiIcon name="plus" />
      </Box>
    </Box>
    <Text type="caption" align="center">
      Min {{ tokenFormatter.format(minTokens) }} tokens ({{ formatAed(minTokens * tokenPrice) }})
      &bull; Max {{ tokenFormatter.format(tokensAvailable) }} available
    </Text>
  </Box>
</template>

<style scoped>
.token-stepper {
  width: 100%;
  background: var(--p-surface-50);
  border: 1px solid var(--p-content-border-color);
  border-radius: 10px;
  padding: 9px 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.token-stepper__btn {
  width: 42px;
  height: 42px;
  background: var(--p-content-border-color);
  border: 1px solid var(--p-surface-300);
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  color: var(--p-text-color);
  transition: background 0.15s;
  flex-shrink: 0;
}

.token-stepper__btn:hover:not(:disabled) {
  background: var(--p-surface-300);
}

.token-stepper__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.token-stepper__count-input {
  font-size: 24px;
  font-weight: 700;
  color: var(--p-text-color);
  line-height: 1.2;
  font-family: inherit;
  width: 100px;
  text-align: center;
  border: none;
  background: transparent;
  outline: none;
  -moz-appearance: textfield;
  appearance: textfield;
}

.token-stepper__count-input::-webkit-outer-spin-button,
.token-stepper__count-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
